import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Zap, Droplets, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { useParkingModule } from '../hooks/useParkingModule';
import { Modal } from '../components/Modal';

interface SpotRow {
  id: number;
  label: string;
  spot_type: string;
  has_electricity: boolean;
  has_water: boolean;
  is_active: boolean;
}

interface SpotForm {
  label: string;
  spot_type: string;
  has_electricity: boolean;
  has_water: boolean;
}

const EMPTY_FORM: SpotForm = {
  label: '',
  spot_type: 'car',
  has_electricity: false,
  has_water: false,
};

const SPOT_TYPES = ['car', 'caravan', 'trailer'] as const;

export function ParkingSpotsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const { parkingEnabled, loading: modLoading } = useParkingModule();
  const [spots, setSpots] = useState<SpotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editSpot, setEditSpot] = useState<SpotRow | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SpotRow | null>(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<SpotRow | null>(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchSpots = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getParkingSpots();
      setSpots(data.spots as unknown as SpotRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => {
    if (parkingEnabled) fetchSpots();
    else setLoading(false);
  }, [fetchSpots, parkingEnabled]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const result = (await api.deleteParkingSpot(deleteTarget.id)) as { warning?: string } | undefined;
      toast.success(t('parkingSpots.spotDeactivated', { label: deleteTarget.label }));
      if (result?.warning) toast.info(result.warning);
      setDeleteTarget(null);
      fetchSpots();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    try {
      await api.deleteParkingSpot(permanentDeleteTarget.id, { permanent: true });
      toast.success(t('parkingSpots.permanentDeleted', { label: permanentDeleteTarget.label }));
      setPermanentDeleteTarget(null);
      fetchSpots();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'PARKING_SPOT_HAS_BOOKINGS') toast.error(t('parkingSpots.permanentDeleteBlockedBookings'));
        else if (err.code === 'MUST_DEACTIVATE') toast.error(t('parkingSpots.permanentDeleteMustDeactivate'));
        else toast.error(err.message);
      } else {
        toast.error(t('errors.somethingWentWrong'));
      }
    }
  };

  if (modLoading || (parkingEnabled && loading)) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!parkingEnabled) {
    return (
      <div className="max-w-lg space-y-3">
        <h1 className="text-xl font-bold text-gray-900">{t('parkingSpots.title')}</h1>
        <p className="text-sm text-gray-600">{t('parkingSpots.moduleDisabled')}</p>
        <RouterLink to="/modules" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700">
          <Link2 className="w-4 h-4" /> {t('parkingSpots.openModules')}
        </RouterLink>
      </div>
    );
  }

  const activeSpots = spots.filter((s) => s.is_active);
  const inactiveSpots = spots.filter((s) => !s.is_active);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('parkingSpots.title')}</h1>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> {t('parkingSpots.addSpot')}
        </button>
      </div>
      <p className="text-sm text-gray-500">{t('parkingSpots.subtitle')}</p>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {activeSpots.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">
            {t('parkingSpots.noSpots')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingSpots.label')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingSpots.spotType')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingSpots.electricity')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingSpots.water')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeSpots.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900">{s.label}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{t(`parkingSpots.types.${s.spot_type}` as Parameters<typeof t>[0])}</span>
                    </td>
                    <td className="px-4 py-3">{s.has_electricity ? <Zap className="w-4 h-4 text-amber-500" /> : '—'}</td>
                    <td className="px-4 py-3">{s.has_water ? <Droplets className="w-4 h-4 text-sky-500" /> : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => setEditSpot(s)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(s)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inactiveSpots.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('parkingSpots.inactiveSpots')}</h3>
          <div className="space-y-2">
            {inactiveSpots.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 text-sm text-gray-600">
                <span className="min-w-0 truncate">{s.label} ({t(`parkingSpots.types.${s.spot_type}` as Parameters<typeof t>[0])})</span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.updateParkingSpot(s.id, { is_active: true });
                        toast.success(t('parkingSpots.spotReactivated', { label: s.label }));
                        fetchSpots();
                      } catch (err) {
                        toast.error(err instanceof ApiError ? err.message : t('parkingSpots.failedToReactivate'));
                      }
                    }}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {t('berths.reactivate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPermanentDeleteTarget(s)}
                    className="text-xs text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('parkingSpots.deletePermanently')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SpotModal
        open={showAdd || !!editSpot}
        onClose={() => { setShowAdd(false); setEditSpot(null); }}
        initial={editSpot}
        onSaved={() => { setShowAdd(false); setEditSpot(null); fetchSpots(); }}
      />

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title={t('parkingSpots.deactivateTitle')} maxWidth="max-w-sm">
        <p className="text-sm text-gray-600 mb-4">{t('parkingSpots.deactivateBody', { label: deleteTarget?.label ?? '' })}</p>
        <div className="flex gap-2">
          <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">{t('common.cancel')}</button>
          <button type="button" onClick={() => { void handleDelete(); }} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium">{t('berths.deactivate')}</button>
        </div>
      </Modal>

      <Modal open={!!permanentDeleteTarget} onClose={() => setPermanentDeleteTarget(null)} title={t('parkingSpots.permanentDeleteTitle')} maxWidth="max-w-sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">{t('parkingSpots.permanentDeleteBody', { label: permanentDeleteTarget?.label ?? '' })}</p>
              <p className="text-xs text-gray-500 mt-2">{t('parkingSpots.permanentDeleteHint')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('common.thisActionCannotBeUndone')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPermanentDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
            <button type="button" onClick={() => { void handlePermanentDelete(); }} className="flex-1 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800">{t('parkingSpots.deletePermanently')}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SpotModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial: SpotRow | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState<SpotForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          label: initial.label,
          spot_type: initial.spot_type,
          has_electricity: initial.has_electricity,
          has_water: initial.has_water,
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, initial]);

  const submit = async () => {
    if (!form.label.trim()) {
      toast.error(t('parkingSpots.labelRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        spot_type: form.spot_type,
        has_electricity: form.has_electricity,
        has_water: form.has_water,
      };
      if (initial) {
        await api.updateParkingSpot(initial.id, payload);
        toast.success(t('parkingSpots.updated'));
      } else {
        await api.createParkingSpot(payload);
        toast.success(t('parkingSpots.created'));
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? t('parkingSpots.editTitle', { label: initial.label }) : t('parkingSpots.addTitle')} maxWidth="max-w-md">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingSpots.label')} *</label>
          <input
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingSpots.spotType')}</label>
          <select
            value={form.spot_type}
            onChange={(e) => setForm((f) => ({ ...f, spot_type: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {SPOT_TYPES.map((st) => (
              <option key={st} value={st}>{t(`parkingSpots.types.${st}` as Parameters<typeof t>[0])}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.has_electricity} onChange={(e) => setForm((f) => ({ ...f, has_electricity: e.target.checked }))} />
          {t('parkingSpots.electricity')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.has_water} onChange={(e) => setForm((f) => ({ ...f, has_water: e.target.checked }))} />
          {t('parkingSpots.water')}
        </label>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">{t('common.cancel')}</button>
          <button type="button" disabled={saving} onClick={() => { void submit(); }} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
